/** Where the language set of the channel the editor is pinned to stands:
 *  being read, held, or not readable because the read failed. */
export type LanguageSetStatus = "reading" | "held" | "failed";

export type SaveRefusal =
  | "scope-moved"
  | "package-closed"
  | "no-permission"
  | "languages-reading"
  | "languages-failed";

/** Precedence: a role that moved outranks a closed package, which outranks
 *  a missing permission, which outranks a language set still being read or
 *  not readable. The first three are conclusive, and the first two hold for
 *  every user of the page; the last passes, or needs a reload, so it is
 *  named only when nothing conclusive blocks the save. */
export function saveRefusal(state: {
  scopeMoved: boolean;
  packageClosed: boolean;
  mayUpdate: boolean;
  languageSet: LanguageSetStatus;
}): SaveRefusal | null {
  if (state.scopeMoved) return "scope-moved";
  if (state.packageClosed) return "package-closed";
  if (!state.mayUpdate) return "no-permission";
  return languageSetRefusal(state.languageSet);
}

/** A write that carries the package's content is built on the pinned
 *  channel's language set: until that set is in, the one held is another
 *  channel's, or an older one. */
export function languageSetRefusal(
  status: LanguageSetStatus,
): SaveRefusal | null {
  if (status === "reading") return "languages-reading";
  if (status === "failed") return "languages-failed";
  return null;
}

export interface ScopeNames {
  readonly role: string;
  readonly channel: string;
  readonly editorChannel: string;
  /** The channel the editor is pinned to, whose language set it is built
   *  on. */
  readonly languageChannel: string;
}

function languageSetOf(names: ScopeNames): string {
  return names.languageChannel
    ? `The language set of ${names.languageChannel}`
    : "The channel's language set";
}

export function saveRefusalMessage(
  refusal: SaveRefusal,
  names: ScopeNames,
): string {
  switch (refusal) {
    case "scope-moved": {
      const role = names.role || "another role";
      const now = names.channel ? `${role} on ${names.channel}` : role;
      const edits = names.editorChannel
        ? `Edits made on ${names.editorChannel}`
        : "These edits";
      return `Your role is now ${now}. ${edits} cannot be saved under it.`;
    }
    case "package-closed":
      return "This package is retired or obsolete and can no longer be saved.";
    case "no-permission":
      return "You do not have permission to save these edits.";
    case "languages-reading":
      return `${languageSetOf(names)} is still being read. Try again in a moment.`;
    case "languages-failed":
      return `${languageSetOf(names)} could not be read. Reload the page to try again.`;
  }
}
